import React, { useState, useEffect } from 'react';
import { createCompanyProfile, uploadCompanyLogo, uploadCompanyImages, addRecruiter } from '../services/companyService';
import { locationsByState, talukasByDistrict } from '../constants/locations';

const CompanyProfileForm = ({ user, onComplete, initialData = null }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    // Basic Information
    companyName: '',
    industry: '',
    companySize: '',
    companyType: '',
    foundedYear: '',
    location: '',
    website: '',
    description: '',

    // Company Details
    mission: '',
    vision: '',
    values: '',
    culture: '',
    benefits: '',

    // Contact Information
    email: '',
    phone: '',
    whatsapp: '',
    address: '',
    city: '',
    state: '',
    district: '',
    taluka: '',
    pincode: '',
    country: 'India',
    zipCode: '',

    // Social Media
    linkedinUrl: '',
    twitterUrl: '',
    facebookUrl: '',
    instagramUrl: '',

    // Images
    logoUrl: '',
    companyImages: [],

    // Recruiters
    recruiters: []
  });

  const [newRecruiter, setNewRecruiter] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    position: '',
    department: '',
    bio: ''
  });

  const [logoFile, setLogoFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [logoPreview, setLogoPreview] = useState('');

  // Populate form with initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));

      if (initialData.logoUrl) {
        setLogoPreview(initialData.logoUrl);
      }
    }
  }, [initialData]);

  const steps = [
    { id: 1, title: 'Basic Information', description: 'Company details and overview', icon: '🏢' },
    { id: 2, title: 'Company Culture', description: 'Mission, vision, and values', icon: '🎯' },
    { id: 3, title: 'Contact & Location', description: 'Contact information and address', icon: '📍' },
    { id: 4, title: 'Media & Branding', description: 'Logo and company images', icon: '🖼️' },
    { id: 5, title: 'Recruiter Team', description: 'Add your recruitment team', icon: '👥' },
    { id: 6, title: 'Review & Submit', description: 'Review and complete profile', icon: '✅' }
  ];

  const industries = [
    'Automobile / Auto Ancillary',
    'Engineering / Fabrication',
    'Foundry / Casting',
    'Plastic / Injection Moulding',
    'Textile / Garments',
    'Food Processing',
    'Packaging Industry',
    'Chemical Industry',
    'Pharma Manufacturing',
    'Electrical / Electronics Manufacturing',
    'Paper / Printing',
    'Steel / Metal Industry',
    'Security Services',
    'Housekeeping Services',
    'Facility Management',
    'Construction / Civil',
    'Maintenance & Repair',
    'Warehouse / Logistics',
    'Courier & Delivery',
    'Driver / Transport Services',
    'Manpower / Staffing Agency',
    'Pest Control Services',
    'Hotel / Hospitality',
    'Restaurant / Cafe / Food Service',
    'Travel & Tourism',
    'Event Management',
    'Salon / Spa / Beauty',
    'Fitness / Gym',
    'Laundry Services',
    'FMCG',
    'Supermarket / Hypermarket',
    'Showroom / Electronics Retail',
    'Jewellery Retail',
    'Mobile Shop / Electronics Shop',
    'Automobile Showroom',
    'BPO / Call Center',
    'KPO / Back Office',
    'E-commerce',
    'IT Services / Software',
    'Digital Marketing Agency',
    'Advertising / Marketing Agency',
    'HR / Recruitment Agency',
    'Hospital / Clinic',
    'Diagnostic Lab',
    'Medical Store / Pharmacy',
    'Medical Equipment',
    'Nursing / Care Services',
    'School',
    'College',
    'Coaching Classes',
    'EdTech / Online Education',
    'Agriculture / Agri Services',
    'Dairy / Poultry',
    'Cold Storage',
    'Seeds / Fertilizer',
    'Agro Processing',
    'Other'
  ];

  const companySizes = [
    '1-10 employees', '11-50 employees', '51-200 employees',
    '201-500 employees', '501-1000 employees', '1001-5000 employees',
    '5000+ employees'
  ];

  const companyTypes = [
    'Public Company', 'Private Company', 'Non-Profit', 'Government',
    'Startup', 'Enterprise', 'SME', 'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStateChange = (e) => {
    const newState = e.target.value;
    setFormData(prev => ({
      ...prev,
      state: newState,
      district: '',
      taluka: ''
    }));
  };

  const handleDistrictChange = (e) => {
    const newDistrict = e.target.value;
    setFormData(prev => ({
      ...prev,
      district: newDistrict,
      taluka: ''
    }));
  };

  const handleRecruiterChange = (e) => {
    const { name, value } = e.target;
    setNewRecruiter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(prev => [...prev, ...files]);
  };

  const addRecruiterToList = () => {
    if (newRecruiter.name && newRecruiter.email) {
      setFormData(prev => ({
        ...prev,
        recruiters: [...prev.recruiters, { ...newRecruiter, id: Date.now() }]
      }));
      setNewRecruiter({
        name: '',
        email: '',
        phone: '',
        whatsapp: '',
        position: '',
        department: '',
        bio: ''
      });
    }
  };

  const removeRecruiter = (id) => {
    setFormData(prev => ({
      ...prev,
      recruiters: prev.recruiters.filter(recruiter => recruiter.id !== id)
    }));
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const calculateProgress = () => {
    const requiredFields = [
      'companyName',
      'industry',
      'companySize',
      'companyType',
      'location',
      'description',
      'email',
      'logoUrl'
    ];

    const totalRequired = requiredFields.length;
    const filledRequired = requiredFields.filter(field => {
      const value = formData[field];
      return value && value !== '' && value !== null && value !== undefined;
    }).length;

    const logoFilled = logoFile || formData.logoUrl ? 1 : 0;
    const adjustedFilled = filledRequired - (formData.logoUrl ? 1 : 0) + logoFilled;

    return Math.round((adjustedFilled / totalRequired) * 100);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!formData.companyName?.trim()) {
        setError('Company Name is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.industry) {
        setError('Industry is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.companySize) {
        setError('Company Size is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.companyType) {
        setError('Company Type is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.location?.trim()) {
        setError('Location is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.description?.trim()) {
        setError('Company Description is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!formData.email?.trim()) {
        setError('Contact Email is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!logoFile && !formData.logoUrl) {
        setError('Company Logo is required');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      const companyId = user.uid;

      if (logoFile) {
        console.log('Uploading logo...');
        const logoResult = await uploadCompanyLogo(logoFile, companyId);
        if (logoResult.success) {
          formData.logoUrl = logoResult.url;
          console.log('Logo uploaded successfully');
        } else {
          console.warn('Logo upload failed:', logoResult.error);
        }
      }

      if (imageFiles.length > 0) {
        console.log('Uploading company images...');
        const imagesResult = await uploadCompanyImages(imageFiles, companyId);
        if (imagesResult.success) {
          formData.companyImages = imagesResult.urls;
          console.log('Company images uploaded successfully');
        } else {
          console.warn('Company images upload failed:', imagesResult.error);
        }
      }

      console.log('Creating company profile...');
      const result = await createCompanyProfile(formData, user.uid);

      if (result.success) {
        setSuccess('Company profile created successfully!');
        if (onComplete) {
          onComplete(result.companyId);
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('An error occurred while creating the profile: ' + error.message);
    }

    setLoading(false);
  };

  const validateStep = (step) => {
    setError('');

    switch (step) {
      case 1:
        if (!formData.companyName?.trim()) {
          setError('Company Name is required');
          return false;
        }
        if (!formData.industry) {
          setError('Industry is required');
          return false;
        }
        if (!formData.companySize) {
          setError('Company Size is required');
          return false;
        }
        if (!formData.companyType) {
          setError('Company Type is required');
          return false;
        }
        if (!formData.location?.trim()) {
          setError('Location is required');
          return false;
        }
        if (!formData.description?.trim()) {
          setError('Company Description is required');
          return false;
        }
        return true;

      case 2:
        return true;

      case 3:
        if (!formData.email?.trim()) {
          setError('Contact Email is required');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }

        if (!formData.state) {
          setError('State is required');
          return false;
        }
        if (!formData.district) {
          setError('District is required');
          return false;
        }
        if (!formData.taluka) {
          setError('Taluka is required');
          return false;
        }
        if (!formData.pincode) {
          setError('Pincode is required');
          return false;
        }
        if (formData.pincode.length !== 6) {
          setError('Pincode must be 6 digits');
          return false;
        }
        return true;

      case 4:
        if (!logoFile && !formData.logoUrl) {
          setError('Company Logo is required');
          return false;
        }
        return true;

      case 5:
        return true;

      case 6:
        return true;

      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  // Get user name for welcome message
  const userName = user?.displayName || user?.email?.split('@')[0] || 'there';

  // ===================== DESIGN UPGRADED RENDER =====================
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Professional Welcome Banner - Purple Color */}
            <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 p-6 rounded-xl shadow-lg border border-purple-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Welcome back, {userName}! 👋</h3>
                  <p className="text-purple-200 text-sm mt-0.5">Manage your job postings and find the right candidates.</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-5 rounded-xl shadow-sm border border-white/50">
              <h3 className="text-xl font-bold text-slate-800">✨ Basic Company Information</h3>
              <p className="text-slate-500 text-sm mt-1">Tell us about your company's basic details</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="group">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Company Name <span className="text-rose-500">*</span></label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} placeholder="Enter your company name" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 bg-white/80 backdrop-blur-sm" required />
              </div>
              <div className="group">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Industry <span className="text-rose-500">*</span></label>
                <select name="industry" value={formData.industry} onChange={handleChange} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-200 bg-white/80">
                  <option value="">Select Industry</option>
                  {industries.map(industry => (<option key={industry} value={industry}>{industry}</option>))}
                </select>
              </div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Company Size <span className="text-rose-500">*</span></label>
                <select name="companySize" value={formData.companySize} onChange={handleChange} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"><option value="">Select Size</option>{companySizes.map(s=><option key={s}>{s}</option>)}</select></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Company Type <span className="text-rose-500">*</span></label>
                <select name="companyType" value={formData.companyType} onChange={handleChange} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl"><option value="">Select Type</option>{companyTypes.map(t=><option key={t}>{t}</option>)}</select></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Founded Year</label><input type="number" name="foundedYear" value={formData.foundedYear} onChange={handleChange} placeholder="2000" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl"/></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Location <span className="text-rose-500">*</span></label><input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="City, State" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl"/></div>
              <div className="md:col-span-2"><label className="block text-sm font-semibold text-slate-700 mb-1.5">Website</label><input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://" className="w-full px-4 py-2.5 border border-slate-200 rounded-xl"/></div>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Company Description <span className="text-rose-500">*</span></label><textarea name="description" value={formData.description} onChange={handleChange} rows={4} placeholder="Tell us about your company..." className="w-full px-4 py-2.5 border border-slate-200 rounded-xl resize-none"></textarea></div>
          </div>
        );
      case 2:
        return (<div className="space-y-5"><div className="bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50 p-5 rounded-xl"><h3 className="text-xl font-bold text-slate-800">🎯 Culture & Values</h3><p className="text-slate-500">Share what makes your company special</p></div>
        <textarea name="mission" value={formData.mission} onChange={handleChange} rows={3} placeholder="Mission Statement" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea>
        <textarea name="vision" value={formData.vision} onChange={handleChange} rows={3} placeholder="Vision Statement" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea>
        <textarea name="values" value={formData.values} onChange={handleChange} rows={3} placeholder="Core Values" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea>
        <textarea name="culture" value={formData.culture} onChange={handleChange} rows={4} placeholder="Work Culture" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea>
        <textarea name="benefits" value={formData.benefits} onChange={handleChange} rows={4} placeholder="Employee Benefits" className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea></div>);
      case 3:
        return (<div className="space-y-6"><div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-xl"><h3 className="text-xl font-bold">📍 Contact & Location</h3></div>
        <div className="grid md:grid-cols-2 gap-4"><input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Contact Email *" className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="tel" name="phone" placeholder="Phone" className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="tel" name="whatsapp" placeholder="WhatsApp" className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/></div>
        <div className="grid md:grid-cols-3 gap-4"><select name="state" value={formData.state} onChange={handleStateChange} className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"><option value="">Select State</option>{Object.keys(locationsByState).map(s=><option key={s}>{s}</option>)}</select>
        <select name="district" value={formData.district} onChange={handleDistrictChange} disabled={!formData.state} className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"><option value="">District</option>{formData.state && locationsByState[formData.state]?.map(d=><option key={d}>{d}</option>)}</select>
        {formData.district && talukasByDistrict[formData.district] ? (<select name="taluka" value={formData.taluka} onChange={handleChange} className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"><option value="">Taluka</option>{talukasByDistrict[formData.district].map(t=><option key={t}>{t}</option>)}</select>) : (<input type="text" name="taluka" value={formData.taluka} onChange={handleChange} placeholder="Taluka *" className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/>)}</div>
        <div className="grid md:grid-cols-2 gap-4"><input type="text" name="pincode" value={formData.pincode} onChange={(e)=>handleChange({target:{name:'pincode',value:e.target.value.replace(/\D/g,'').slice(0,6)}})} placeholder="Pincode *" className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><textarea name="address" rows={2} placeholder="Address" className="p-2.5 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea></div>
        <div className="grid md:grid-cols-2 gap-3"><input type="url" name="linkedinUrl" placeholder="LinkedIn URL" className="p-2 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="url" name="twitterUrl" placeholder="Twitter URL" className="p-2 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="url" name="facebookUrl" placeholder="Facebook URL" className="p-2 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="url" name="instagramUrl" placeholder="Instagram URL" className="p-2 border rounded-xl focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/></div></div>);
      case 4:
        return (<div className="space-y-6"><div className="bg-gradient-to-br from-amber-50 to-orange-50 p-5 rounded-xl"><h3 className="text-xl font-bold">🖼️ Logo & Images</h3></div>
        <div className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center bg-purple-50/30">{logoPreview ? (<div><img src={logoPreview} className="h-28 w-28 object-contain mx-auto rounded-lg shadow"/><button onClick={()=>{setLogoFile(null);setLogoPreview('')}} className="text-red-500 text-sm mt-2">Remove</button></div>) : (<><input id="logo" type="file" accept="image/*" onChange={handleLogoChange} className="hidden"/><label htmlFor="logo" className="cursor-pointer bg-purple-600 text-white px-5 py-2 rounded-full shadow-sm hover:bg-purple-700 transition">Upload Logo *</label><p className="text-xs text-slate-400 mt-2">PNG, JPG upto 10MB</p></>)}</div>
        <div className="border-2 border-dashed border-purple-300 rounded-xl p-6 text-center bg-purple-50/30"><input id="images" type="file" multiple accept="image/*" onChange={handleImagesChange} className="hidden"/><label htmlFor="images" className="cursor-pointer bg-purple-600 text-white px-5 py-2 rounded-full shadow hover:bg-purple-700 transition">Add Company Images</label>{imageFiles.length>0 && <div className="grid grid-cols-4 gap-3 mt-4">{imageFiles.map((f,i)=><div key={i} className="relative"><img src={URL.createObjectURL(f)} className="h-20 w-full object-cover rounded"/><button onClick={()=>removeImage(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs">x</button></div>)}</div>}</div></div>);
      case 5:
        return (<div className="space-y-5"><div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-5 rounded-xl"><h3 className="text-xl font-bold">👥 Recruiter Team</h3></div>
        <div className="bg-white rounded-xl border p-5 shadow-sm"><div className="grid md:grid-cols-2 gap-3"><input type="text" name="name" value={newRecruiter.name} onChange={handleRecruiterChange} placeholder="Name *" className="p-2 border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="email" name="email" value={newRecruiter.email} onChange={handleRecruiterChange} placeholder="Email *" className="p-2 border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="tel" name="phone" placeholder="Phone" className="p-2 border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="tel" name="whatsapp" placeholder="WhatsApp" className="p-2 border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="text" name="position" placeholder="Position" className="p-2 border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/><input type="text" name="department" placeholder="Dept" className="p-2 border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"/></div><textarea name="bio" rows={2} placeholder="Bio" className="w-full p-2 border rounded-lg mt-2 focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"></textarea><button onClick={addRecruiterToList} className="mt-3 bg-purple-600 text-white px-5 py-2 rounded-xl shadow-md hover:bg-purple-700 transition">+ Add Recruiter</button></div>
        {formData.recruiters.length>0 && <div className="space-y-2">{formData.recruiters.map(r=><div key={r.id} className="flex justify-between items-center bg-purple-50 p-3 rounded-xl border border-purple-200"><div><p className="font-bold text-purple-900">{r.name}</p><p className="text-sm text-purple-600">{r.email}</p></div><button onClick={()=>removeRecruiter(r.id)} className="text-red-500 text-sm hover:text-red-700">Remove</button></div>)}</div>}</div>);
      case 6:
        const prog = calculateProgress();
        return (<div className="space-y-6"><div className="bg-gradient-to-br from-purple-50 to-violet-50 p-5 rounded-xl"><h3 className="text-xl font-bold text-purple-900">✅ Review & Submit</h3></div>
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-5 rounded-xl"><div className="flex justify-between items-center"><span className="font-bold text-purple-900">Profile Completion</span><span className="text-purple-700 font-mono text-xl">{prog}%</span></div><div className="w-full bg-gray-200 rounded-full h-2 mt-2"><div className="bg-gradient-to-r from-purple-600 to-violet-600 h-2 rounded-full" style={{width:`${prog}%`}}></div></div></div>
        <div className="grid gap-3"><details className="border border-purple-200 rounded-xl p-4 bg-white"><summary className="font-bold cursor-pointer text-purple-900">🏢 Basic Info</summary><div className="mt-2 text-sm text-slate-600">{formData.companyName || '—'} | {formData.industry || '—'}</div></details>
        <details className="border border-purple-200 rounded-xl p-4"><summary className="font-bold cursor-pointer text-purple-900">🎯 Culture</summary><div className="mt-2 text-sm text-slate-500">{formData.mission || 'Not provided'}</div></details>
        <details className="border border-purple-200 rounded-xl p-4"><summary className="font-bold cursor-pointer text-purple-900">📞 Contact</summary><div className="text-slate-600">{formData.email} | {formData.state} - {formData.pincode}</div></details>
        <details className="border border-purple-200 rounded-xl p-4"><summary className="font-bold cursor-pointer text-purple-900">🖼️ Media</summary><div className="text-slate-600">Logo: {logoPreview?'✅':'❌'} | Images: {imageFiles.length}</div></details>
        <details className="border border-purple-200 rounded-xl p-4"><summary className="font-bold cursor-pointer text-purple-900">👥 Recruiters</summary><div className="text-slate-600">{formData.recruiters.length} recruiters added</div></details></div>
        {error && <div className="bg-red-50 text-red-700 p-3 rounded-xl border-l-4 border-red-500">{error}</div>}
        {success && <div className="bg-green-50 text-green-700 p-3 rounded-xl border-l-4 border-green-500">{success}</div>}</div>);
      default: return null;
    }
  };

  return (
    <div className="min-h-screen py-10 px-4" style={{background: 'radial-gradient(circle at 10% 20%, rgba(249,250,251,1) 0%, rgba(241,245,249,1) 100%)'}}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex p-4 bg-gradient-to-tr from-purple-700 to-purple-800 rounded-3xl shadow-2xl mb-4">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-4xl font-black bg-gradient-to-r from-purple-800 to-purple-600 bg-clip-text text-transparent">Company Profile Studio</h1>
          <p className="text-slate-500 mt-1">Complete your profile to attract top talent</p>
        </div>

        <div className="relative flex justify-between mb-12">
          {steps.map((s,idx)=>(
            <div key={s.id} className="flex flex-col items-center flex-1">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-md ${currentStep>=s.id?'bg-gradient-to-br from-purple-600 to-purple-700 text-white scale-110':'bg-white border-2 border-slate-200 text-slate-400'}`}>
                <span className="text-xl">{s.icon}</span>
              </div>
              <div className="text-xs font-semibold mt-2 hidden md:block">
                <span className={currentStep>=s.id?'text-purple-700':'text-slate-400'}>{s.title}</span>
              </div>
              {idx<steps.length-1 && <div className={`absolute top-5 left-1/2 w-[calc(100%-3rem)] h-0.5 -z-0 transition-all ${currentStep>s.id?'bg-gradient-to-r from-purple-600 to-purple-700':'bg-slate-200'}`} style={{left:'50%'}}/>}
            </div>
          ))}
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 overflow-hidden">
          <div className="p-6 md:p-8">{renderStepContent()}</div>
          <div className="bg-slate-50/80 px-6 py-4 flex justify-between gap-3 border-t">
            <button onClick={prevStep} disabled={currentStep===1} className="px-6 py-2 rounded-xl border-2 border-slate-300 text-slate-700 font-medium disabled:opacity-50 hover:bg-slate-100 transition">← Previous</button>
            {currentStep<steps.length?<button onClick={nextStep} className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium shadow-md hover:shadow-xl transition-all hover:scale-[1.02]">Next Step →</button>
            :<button onClick={handleSubmit} disabled={loading} className="px-8 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium shadow-lg hover:shadow-xl transition-all">{loading?'Creating...':'✨ Publish Profile ✨'}</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileForm;